import React, { useState, useEffect } from "react";
import { collection, getDocs, orderBy, query, getDoc, doc, addDoc, serverTimestamp, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { reviewBase } from "../shop";

const ORDER_REVIEW_BASE = `${reviewBase()}/order-review`;

const formatTs = (timestamp) => {
  if (!timestamp) return "—";
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return d.toLocaleDateString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
};

const getStatus = (order) => {
  if (!order.orderReviewedAt) return "pending";
  if (order.orderDecision === "modification") return "modification";
  return "confirmed";
};

const STATUS_STYLES = {
  pending:      { background: "#fefcbf", color: "#744210", border: "1px solid #f6e05e" },
  modification: { background: "#ffecd2", color: "#7b341e", border: "1px solid #f6ad55" },
  confirmed:    { background: "#c6f6d5", color: "#22543d", border: "1px solid #68d391" },
};
const STATUS_LABELS = {
  pending:      "Ausstehend",
  modification: "Änderungswunsch",
  confirmed:    "Bestätigt",
};

const StatusBadge = ({ status, onClick }) => (
  <span
    onClick={onClick}
    style={{
      ...STATUS_STYLES[status],
      padding: "0.2em 0.65em",
      borderRadius: "12px",
      fontSize: "0.78rem",
      fontWeight: "600",
      whiteSpace: "nowrap",
      cursor: status === "modification" ? "pointer" : "default",
      textDecoration: status === "modification" ? "underline dotted" : "none",
    }}
    title={status === "modification" ? "Klicken für Änderungsdetails" : undefined}
  >
    {STATUS_LABELS[status]}
  </span>
);

const ErrorBadgeWithRetry = ({ order, retrying, onRetry }) => (
  <>
    <span
      title={order.reminderError}
      style={{
        marginLeft: "0.4em",
        padding: "0.1em 0.45em",
        borderRadius: "10px",
        border: "1px solid #fc8181",
        background: "#fff5f5",
        color: "#c53030",
        fontSize: "0.72rem",
        fontWeight: "600",
        whiteSpace: "nowrap",
        cursor: "help",
      }}
    >
      ⚠ E-Mail Fehler
    </span>
    <button
      onClick={() => onRetry(order)}
      disabled={!!retrying[order.id]}
      style={{
        marginLeft: "0.4em",
        padding: "0.1em 0.5em",
        borderRadius: "10px",
        border: "1px solid #90cdf4",
        background: "#ebf8ff",
        color: "#2b6cb0",
        fontSize: "0.72rem",
        fontWeight: "600",
        whiteSpace: "nowrap",
        cursor: retrying[order.id] ? "default" : "pointer",
        opacity: retrying[order.id] ? 0.6 : 1,
      }}
    >
      {retrying[order.id] ? "…" : "↺ Erneut"}
    </button>
  </>
);

// ── Modification detail modal ──────────────────────────────────────────────────

const ModificationModal = ({ order, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "1em",
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: "#fff", borderRadius: "12px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        width: "100%", maxWidth: "520px",
        padding: "2em",
      }}
    >
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", marginBottom: "1.25em",
      }}>
        <div>
          <h3 style={{ margin: "0 0 0.3em", fontSize: "1.1rem", color: "#1a202c" }}>
            Änderungswunsch
          </h3>
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#718096" }}>
            {order.buyerName && <span>{order.buyerName} · </span>}
            {order.customerEmail && <span>{order.customerEmail} · </span>}
            Bestellung #{order.orderId}
          </p>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none", border: "none", fontSize: "1.4rem",
            color: "#a0aec0", cursor: "pointer", lineHeight: 1,
            padding: "0 0.2em", marginLeft: "1em",
          }}
          aria-label="Schließen"
        >
          ×
        </button>
      </div>

      {/* Received date */}
      <p style={{ margin: "0 0 1em", fontSize: "0.82rem", color: "#a0aec0" }}>
        Eingegangen: {formatTs(order.orderReviewedAt)}
      </p>

      {/* Feedback text */}
      <div style={{
        background: "#fdf7f4", border: "1px solid #e2e8f0",
        borderRadius: "8px", padding: "1em 1.25em",
        minHeight: "80px",
      }}>
        {order.orderFeedback ? (
          <p style={{
            margin: 0, color: "#2d3748", lineHeight: "1.7",
            whiteSpace: "pre-wrap", fontStyle: "italic",
          }}>
            „{order.orderFeedback}"
          </p>
        ) : (
          <p style={{ margin: 0, color: "#a0aec0", fontStyle: "italic" }}>
            Kein schriftliches Feedback angegeben.
          </p>
        )}
      </div>

      {/* Footer */}
      <div style={{ marginTop: "1.5em", textAlign: "right" }}>
        <button
          onClick={onClose}
          style={{
            padding: "0.5em 1.4em", borderRadius: "6px",
            border: "1px solid #cbd5e0", background: "#fff",
            color: "#4a5568", fontWeight: "600", fontSize: "0.875rem",
            cursor: "pointer",
          }}
        >
          Schließen
        </button>
      </div>
    </div>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────────

const OrderConfirmationsPage = () => {
  const [orders,         setOrders]         = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [filter,         setFilter]         = useState("all");
  const [search,         setSearch]         = useState("");
  const [modal,          setModal]          = useState(null);
  const [retrying,       setRetrying]       = useState({});
  const [problemDomains,  setProblemDomains]  = useState([]);
  const [etsyQueueMap,    setEtsyQueueMap]    = useState({}); // orderId → queue doc data
  const [retryingEtsy,    setRetryingEtsy]    = useState({}); // orderId → true

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const [snapshot, cfgSnap, queueSnap] = await Promise.all([
          getDocs(query(collection(db, "etsy_orders"), orderBy("welcomeSentAt", "desc"))),
          getDoc(doc(db, "config", "syncSettings")),
          getDocs(query(
            collection(db, "etsy_message_queue"),
            where("messageType", "==", "order_welcome"),
          )),
        ]);
        const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Only show orders with a welcome email sent AND still in "new" stage.
        // If the order moved to a later stage (e.g. grafiker) — because the
        // customer confirmed via Etsy messaging and we changed the stage manually —
        // it is no longer waiting for a review and should not appear here.
        setOrders(docs.filter(
          (o) => !!o.welcomeSentAt && (!o.workflowStage || o.workflowStage === "new")
        ));
        setProblemDomains((cfgSnap.exists() ? cfgSnap.data().problemDomains : null) || []);
        // Build map of most-recent queue entry per order (prefer error/pending over done)
        const qmap = {};
        queueSnap.docs.forEach((d) => {
          const data = { id: d.id, ...d.data() };
          const oid = data.orderId;
          if (!oid) return;
          const existing = qmap[oid];
          // Keep the most recent entry (highest createdAt)
          if (!existing || (data.createdAt?.toMillis?.() || 0) > (existing.createdAt?.toMillis?.() || 0)) {
            qmap[oid] = data;
          }
        });
        setEtsyQueueMap(qmap);
      } catch (err) {
        console.error("Fehler beim Laden der Bestellungen:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const isDomainProblem = (email) => {
    if (!email || !problemDomains.length) return false;
    const domain = email.split("@")[1]?.toLowerCase() || "";
    return problemDomains.some((d) => domain === d || domain.endsWith("." + d));
  };

  const handlePostToEtsy = async (order) => {
    try {
      const newDoc = await addDoc(collection(db, "etsy_message_queue"), {
        orderId:     order.id,
        messageType: "order_welcome",
        status:      "pending",
        createdAt:   serverTimestamp(),
      });
      setEtsyQueueMap((prev) => ({
        ...prev,
        [order.id]: { id: newDoc.id, orderId: order.id, messageType: "order_welcome", status: "pending" },
      }));
    } catch (err) {
      alert(`Fehler: ${err.message}`);
    }
  };

  const handleRetryEtsy = async (order) => {
    setRetryingEtsy((r) => ({ ...r, [order.id]: true }));
    try {
      const newDoc = await addDoc(collection(db, "etsy_message_queue"), {
        orderId:     order.id,
        messageType: "order_welcome",
        status:      "pending",
        createdAt:   serverTimestamp(),
      });
      setEtsyQueueMap((prev) => ({
        ...prev,
        [order.id]: { id: newDoc.id, orderId: order.id, messageType: "order_welcome", status: "pending" },
      }));
    } catch (err) {
      alert(`Fehler: ${err.message}`);
    } finally {
      setRetryingEtsy((r) => ({ ...r, [order.id]: false }));
    }
  };

  const handleRetry = async (order) => {
    setRetrying((r) => ({ ...r, [order.id]: true }));
    try {
      const fn = httpsCallable(
        getFunctions(undefined, "europe-west1"),
        "retryOrderReminder",
      );
      await fn({ orderId: order.id });
      const snap = await getDoc(doc(db, "etsy_orders", order.id));
      if (snap.exists()) {
        setOrders((prev) =>
          prev.map((o) => (o.id === order.id ? { id: snap.id, ...snap.data() } : o))
        );
      }
    } catch (err) {
      alert(`Fehler beim erneuten Senden: ${err.message}`);
    } finally {
      setRetrying((r) => ({ ...r, [order.id]: false }));
    }
  };

  const searchTerm = search.trim().toLowerCase();

  const filtered = orders.filter((o) => {
    if (filter !== "all" && getStatus(o) !== filter) return false;
    if (searchTerm) {
      // Auf o.id zurueckfallen: bis 2026-09-11 legte der Scraper Bestellungen
      // ohne das Feld orderId an, die Nummer stand nur in der Dokument-ID.
      // Ohne diesen Rueckfall waren solche Bestellungen zwar in der Liste,
      // liessen sich aber nicht suchen.
      const matchesOrder  = (o.orderId || o.id || "")
        .toLowerCase().includes(searchTerm);
      const matchesBuyer  = (o.buyerName  || "").toLowerCase().includes(searchTerm);
      const matchesEmail  = (o.customerEmail || "").toLowerCase().includes(searchTerm);
      if (!matchesOrder && !matchesBuyer && !matchesEmail) return false;
    }
    return true;
  });

  const counts = {
    all:          orders.length,
    pending:      orders.filter((o) => getStatus(o) === "pending").length,
    confirmed:    orders.filter((o) => getStatus(o) === "confirmed").length,
    modification: orders.filter((o) => getStatus(o) === "modification").length,
  };

  const pillStyle = (active) => ({
    padding: "0.4em 1.1em",
    borderRadius: "6px",
    border: "1px solid #cbd5e0",
    background: active ? "#4a2c2a" : "#fff",
    color: active ? "#fff" : "#4a5568",
    fontWeight: "600",
    fontSize: "0.85rem",
    cursor: "pointer",
  });

  const FILTERS = [
    { key: "all",          label: `Alle (${counts.all})` },
    { key: "pending",      label: `Ausstehend (${counts.pending})` },
    { key: "confirmed",    label: `Bestätigt (${counts.confirmed})` },
    { key: "modification", label: `Änderungswunsch (${counts.modification})` },
  ];

  const HEADERS = [
    "Bestellt am", "Bestellung #", "Käufer", "E-Mail",
    "Status", "Erin. 1", "Erin. 2", "Erin. 3", "Antwort am", "Link",
  ];

  return (
    <div style={{ maxWidth: 1300, margin: "0 auto" }}>
      {/* Header row */}
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "1em", gap: "1em", flexWrap: "wrap",
      }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", color: "#1a202c" }}>
          Bestellbestätigung
        </h2>
        <div style={{ display: "flex", gap: "0.5em", flexWrap: "wrap" }}>
          {FILTERS.map(({ key, label }) => (
            <button key={key} style={pillStyle(filter === key)}
              onClick={() => setFilter(key)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Search bar */}
      <div style={{ marginBottom: "1.25em" }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suche nach Bestellnummer, Käufer oder E-Mail…"
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "0.55em 0.9em",
            border: "1px solid #cbd5e0", borderRadius: "6px",
            fontSize: "0.875rem", color: "#2d3748",
            background: "#fff", outline: "none",
          }}
        />
      </div>

      {loading ? (
        <p style={{ color: "#718096", fontStyle: "italic" }}>
          Lade Bestellbestätigungen…
        </p>
      ) : filtered.length === 0 ? (
        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          padding: "3em", textAlign: "center", color: "#718096",
        }}>
          <p style={{ fontSize: "1.05rem", margin: 0 }}>
            {orders.length === 0
              ? "Noch keine Welcome-Mails gesendet."
              : `Keine Bestellungen für diesen Filter.`}
          </p>
        </div>
      ) : (
        <div style={{
          background: "#ffffff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          overflowX: "auto",
        }}>
          <table style={{
            width: "100%", borderCollapse: "collapse",
            fontSize: "0.875rem",
          }}>
            <thead>
              <tr style={{ background: "#fdf7f4", borderBottom: "2px solid #e2e8f0" }}>
                {HEADERS.map((h, i) => (
                  <th key={i} style={{
                    padding: "0.75em 1em", textAlign: "left",
                    fontWeight: "700", color: "#4a5568", fontSize: "0.75rem",
                    letterSpacing: "0.05em", textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((order, i) => {
                const status = getStatus(order);
                return (
                  <tr
                    key={order.id}
                    style={{
                      borderBottom: "1px solid #edf2f7",
                      background: i % 2 === 0 ? "#fff" : "#fafafa",
                    }}
                  >
                    {/* Bestellt am */}
                    <td style={{
                      padding: "0.85em 1em", color: "#718096",
                      whiteSpace: "nowrap", fontSize: "0.82rem",
                    }}>
                      {order.orderDate || "—"}
                    </td>
                    {/* Bestellung # */}
                    <td style={{
                      padding: "0.85em 1em", color: "#2d3748", fontWeight: "600",
                    }}>
                      {order.orderId || order.id || (
                        <span style={{ color: "#a0aec0" }}>—</span>
                      )}
                    </td>
                    {/* Käufer */}
                    <td style={{ padding: "0.85em 1em", color: "#2d3748" }}>
                      {order.buyerName || (
                        <span style={{ color: "#a0aec0" }}>—</span>
                      )}
                    </td>
                    {/* E-Mail */}
                    <td style={{ padding: "0.85em 1em", color: "#4a5568" }}>
                      {order.customerEmail || <span style={{ color: "#a0aec0" }}>—</span>}
                      {isDomainProblem(order.customerEmail) && (() => {
                        const qentry = etsyQueueMap[order.id];
                        if (order.etsyWelcomeSentAt) {
                          return (
                            <span style={{
                              display: "inline-block", marginLeft: "6px",
                              padding: "0.15em 0.5em", borderRadius: "10px",
                              background: "#ebf8ff", color: "#2b6cb0",
                              border: "1px solid #90cdf4",
                              fontSize: "0.72rem", fontWeight: "600",
                            }}>Etsy ✓</span>
                          );
                        }
                        if (qentry?.status === "error") {
                          return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", marginLeft: "6px" }}>
                              <span
                                title={`Etsy-Nachricht an #${order.id}${order.buyerName ? ` (${order.buyerName})` : ""} nicht gesendet.\n${qentry.errorMsg ? `Grund: ${qentry.errorMsg}` : "Ein Grund wurde nicht aufgezeichnet."}\nText zum Kopieren und „Erledigt“ im Dashboard.`}
                                style={{
                                  padding: "0.15em 0.5em", borderRadius: "10px",
                                  background: "#fff5f5", color: "#c53030",
                                  border: "1px solid #fc8181",
                                  fontSize: "0.72rem", fontWeight: "600", cursor: "help",
                                }}>Etsy ✗</span>
                              <button
                                onClick={() => handleRetryEtsy(order)}
                                disabled={!!retryingEtsy[order.id]}
                                style={{
                                  padding: "0.15em 0.5em", borderRadius: "10px",
                                  background: "#ebf8ff", color: "#2b6cb0",
                                  border: "1px solid #90cdf4",
                                  fontSize: "0.72rem", fontWeight: "600",
                                  cursor: retryingEtsy[order.id] ? "default" : "pointer",
                                  opacity: retryingEtsy[order.id] ? 0.6 : 1,
                                }}>
                                {retryingEtsy[order.id] ? "…" : "↺ Erneut"}
                              </button>
                            </span>
                          );
                        }
                        if (qentry?.status === "pending") {
                          return (
                            <span style={{
                              display: "inline-block", marginLeft: "6px",
                              padding: "0.15em 0.5em", borderRadius: "10px",
                              background: "#fefcbf", color: "#744210",
                              border: "1px solid #f6e05e",
                              fontSize: "0.72rem", fontWeight: "600",
                            }}>Etsy ausstehend</span>
                          );
                        }
                        return (
                          <button
                            onClick={() => handlePostToEtsy(order)}
                            title="Bestätigungsanfrage direkt über Etsy-Nachrichten senden"
                            style={{
                              display: "inline-block", marginLeft: "6px",
                              padding: "0.15em 0.5em", borderRadius: "10px",
                              background: "#ebf8ff", color: "#2b6cb0",
                              border: "1px solid #90cdf4",
                              fontSize: "0.72rem", fontWeight: "600", cursor: "pointer",
                            }}>
                            Auf Etsy posten
                          </button>
                        );
                      })()}
                    </td>
                    {/* Status */}
                    <td style={{ padding: "0.85em 1em" }}>
                      <StatusBadge
                        status={status}
                        onClick={status === "modification"
                          ? () => setModal(order)
                          : undefined}
                      />
                    </td>
                    {/* Erin. 1 */}
                    <td style={{
                      padding: "0.85em 1em", color: "#718096",
                      fontSize: "0.82rem", whiteSpace: "nowrap",
                    }}>
                      {formatTs(order.orderReminder1SentAt)}
                      {order.reminderError && !order.orderReminder1SentAt && (
                        <ErrorBadgeWithRetry order={order} retrying={retrying} onRetry={handleRetry} />
                      )}
                    </td>
                    {/* Erin. 2 */}
                    <td style={{
                      padding: "0.85em 1em", color: "#718096",
                      fontSize: "0.82rem", whiteSpace: "nowrap",
                    }}>
                      {formatTs(order.orderReminder2SentAt)}
                      {(order.etsyMessage2SentAt ||
                        (!order.etsyMessage2SentAt && !order.etsyMessage3SentAt &&
                         order.etsyMessageSentAt && !order.orderReminder3SentAt)) && (
                        <span style={{
                          marginLeft: "0.4em",
                          padding: "0.1em 0.5em",
                          borderRadius: "10px",
                          border: "1px solid #b794f4",
                          background: "#e9d8fd",
                          color: "#553c9a",
                          fontSize: "0.72rem",
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}>
                          Etsy Nachricht
                        </span>
                      )}
                      {order.reminderError && order.orderReminder1SentAt && !order.orderReminder2SentAt && (
                        <ErrorBadgeWithRetry order={order} retrying={retrying} onRetry={handleRetry} />
                      )}
                    </td>
                    {/* Erin. 3 */}
                    <td style={{
                      padding: "0.85em 1em", color: "#718096",
                      fontSize: "0.82rem", whiteSpace: "nowrap",
                    }}>
                      {formatTs(order.orderReminder3SentAt)}
                      {(order.etsyMessage3SentAt ||
                        (!order.etsyMessage2SentAt && !order.etsyMessage3SentAt &&
                         order.etsyMessageSentAt && order.orderReminder3SentAt)) && (
                        <span style={{
                          marginLeft: "0.4em",
                          padding: "0.1em 0.5em",
                          borderRadius: "10px",
                          border: "1px solid #b794f4",
                          background: "#e9d8fd",
                          color: "#553c9a",
                          fontSize: "0.72rem",
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}>
                          Etsy Nachricht
                        </span>
                      )}
                      {order.reminderError && order.orderReminder2SentAt && !order.orderReminder3SentAt && (
                        <ErrorBadgeWithRetry order={order} retrying={retrying} onRetry={handleRetry} />
                      )}
                    </td>
                    {/* Antwort am */}
                    <td style={{
                      padding: "0.85em 1em", color: "#718096",
                      fontSize: "0.82rem", whiteSpace: "nowrap",
                    }}>
                      {formatTs(order.orderReviewedAt)}
                    </td>
                    {/* Link */}
                    <td style={{ padding: "0.85em 1em" }}>
                      <a
                        href={`${ORDER_REVIEW_BASE}/${order.reviewToken || order.id}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          padding: "0.3em 0.75em", borderRadius: "5px",
                          border: "1px solid #cbd5e0", background: "#fff",
                          color: "#4a2c2a", fontSize: "0.8rem",
                          fontWeight: "500", textDecoration: "none",
                          whiteSpace: "nowrap", display: "inline-block",
                        }}
                      >
                        Link ↗
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={{
        marginTop: "1em", color: "#a0aec0",
        fontSize: "0.8rem", textAlign: "right",
      }}>
        {filtered.length} von {orders.length} Bestellung{orders.length !== 1 ? "en" : ""}
      </p>

      {/* Modification modal */}
      {modal && (
        <ModificationModal order={modal} onClose={() => setModal(null)} />
      )}
    </div>
  );
};

export default OrderConfirmationsPage;
